// GAVION A10: Profile « Changer le mot de passe » (desktop and mobile). In place, with the signed-in session;
// hidden for a Microsoft-only account, whose password lives in Microsoft 365.
import { useMutation, useQuery } from "@tanstack/react-query";
import { KeyRound } from "lucide-react";
import { useDataProvider, useNotify, useTranslate } from "ra-core";
import { useId, useState, type FormEvent } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { CrmDataProvider } from "../../atomic-crm/providers/types";
import { MIN_PASSWORD_LENGTH, passwordErrorCode } from "./passwordMethods";

const K = "gavion.profile_password";

export const ChangePasswordButton = ({
  mobile = false,
}: {
  mobile?: boolean;
}) => {
  const t = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const passwordId = useId();
  const confirmId = useId();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);

  const passwordSignIn = useQuery({
    queryKey: ["gavion", "profile", "password-sign-in"],
    queryFn: () => dataProvider.hasPasswordSignIn(),
    staleTime: Infinity,
    retry: false,
  });

  const change = useMutation({
    mutationFn: (p: string) => dataProvider.changeOwnPassword(p),
    onSuccess: () => {
      notify(`${K}.success`, { type: "success" });
      setOpen(false);
    },
    onError: (e) =>
      setError(
        t(`${K}.errors.${passwordErrorCode(e)}`, { min: MIN_PASSWORD_LENGTH }),
      ),
  });

  if (passwordSignIn.data === false) {
    return (
      <p className="text-sm text-muted-foreground">
        {t(`${K}.microsoft_only`)}
      </p>
    );
  }

  const reset = (next: boolean) => {
    setOpen(next);
    setPassword("");
    setConfirm("");
    setError(null);
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(t(`${K}.errors.too_short`, { min: MIN_PASSWORD_LENGTH }));
      return;
    }
    if (password !== confirm) {
      setError(t(`${K}.errors.mismatch`));
      return;
    }
    setError(null);
    change.mutate(password);
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        className={mobile ? "w-full text-base h-auto" : undefined}
        onClick={() => reset(true)}
      >
        {mobile && <KeyRound className="size-5 mr-3" />}
        {t(`${K}.button`)}
      </Button>
      <Dialog open={open} onOpenChange={reset}>
        <DialogContent>
          <form onSubmit={submit} className="flex flex-col gap-4">
            <DialogHeader>
              <DialogTitle>{t(`${K}.title`)}</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col gap-2">
              <Label htmlFor={passwordId}>{t(`${K}.new_password`)}</Label>
              <Input
                id={passwordId}
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor={confirmId}>{t(`${K}.confirm_password`)}</Label>
              <Input
                id={confirmId}
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {t(`${K}.hint`, { min: MIN_PASSWORD_LENGTH })}
            </p>
            {error && (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            )}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => reset(false)}
              >
                {t(`${K}.cancel`)}
              </Button>
              <Button type="submit" disabled={change.isPending}>
                {t(`${K}.save`)}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};
